'use client'

// Development tenant configuration
export interface DevTenantConfig {
  slug: string
  name: string
  plan_type: 'FREE' | 'BASIC' | 'PRO' | 'ENTERPRISE'
  onboarding_status: 'registration' | 'verification' | 'setup' | 'complete'
  description: string
}

export const DEV_TENANTS: DevTenantConfig[] = [
  {
    slug: 'default',
    name: 'Default Jewelry Store',
    plan_type: 'FREE',
    onboarding_status: 'complete',
    description: 'Fully set up store with sample data'
  },
  {
    slug: 'default-2',
    name: 'Second Test Store',
    plan_type: 'BASIC',
    onboarding_status: 'setup',
    description: 'Partially set up store'
  },
  {
    slug: 'luxury-gems',
    name: 'Luxury Gems Store',
    plan_type: 'PRO',
    onboarding_status: 'verification',
    description: 'Premium store in verification phase'
  },
  {
    slug: 'budget-jewelry',
    name: 'Budget Jewelry Store',
    plan_type: 'FREE',
    onboarding_status: 'registration',
    description: 'New store in registration phase'
  },
  {
    slug: 'premium-jewelers',
    name: 'Premium Jewelers',
    plan_type: 'ENTERPRISE',
    onboarding_status: 'complete',
    description: 'Enterprise store with full features'
  }
]

export const getTenantFromLocalhost = (): string | null => {
  if (typeof window === 'undefined') return null
  
  // Check URL parameter first
  const urlParams = new URLSearchParams(window.location.search)
  const tenantParam = urlParams.get('tenant')
  if (tenantParam) return tenantParam
  
  // Check subdomain simulation
  const hostname = window.location.hostname
  if (hostname.includes('.localhost')) {
    return hostname.split('.')[0]
  }
  
  // Check cookie for development
  const cookieTenant = getCookie('dev_tenant')
  if (cookieTenant) return cookieTenant
  
  // Default fallback for development
  return process.env.NODE_ENV === 'development' ? 'default' : null
}

export const setCookie = (name: string, value: string, days: number = 7): void => {
  if (typeof window === 'undefined') return
  
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`
}

export const getCookie = (name: string): string | null => {
  if (typeof window === 'undefined') return null
  
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

export const deleteCookie = (name: string): void => {
  if (typeof window === 'undefined') return
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`
}

export const isDevelopmentMode = (): boolean => {
  return process.env.NODE_ENV === 'development'
}

export const getDevTenantInfo = (slug: string): DevTenantConfig | undefined => {
  return DEV_TENANTS.find(tenant => tenant.slug === slug)
}

export const switchDevTenant = (tenantSlug: string): void => {
  if (!isDevelopmentMode()) return
  
  setCookie('dev_tenant', tenantSlug)
  window.location.reload()
}

export const resetDevTenant = (): void => {
  if (!isDevelopmentMode()) return
  
  deleteCookie('dev_tenant')
  window.location.reload()
} 